import { Alert, Autocomplete, Stack } from '@mui/material'
import React, { useState } from 'react'

import PageSection, { ErrorSection } from 'components/common/PageSection'
import { useGetGroupedK8sOptions } from 'components/services/create/target-selection/kubernetes/hooks/use-get-grouped-k8s-options'
import { useGetK8sOptionsList } from 'components/services/create/target-selection/kubernetes/hooks/use-get-k8s-options-list'
import { KubernetesTagLabelSection } from 'components/services/create/target-selection/kubernetes/KubernetesTagLabelSection'
import { getCommonK8sObjectsAutocompleteProps } from 'components/services/create/target-selection/kubernetes/service-kubernetes-targeting-selection-utils'
import { ServiceKubernetesTargetingSelectionFooter } from 'components/services/create/target-selection/kubernetes/ServiceKubernetesTargetingSelectionFooter'
import { useGetKubernetesTargetsByOwnerId } from 'hooks/use-get-kubernetes-targets-by-owner-id'
import { useGetKubernetesTargetsKinds } from 'queries/kubernetes/use-get-kubernetes-targets-kinds'
import theme from 'theme'

import type {
  K8sAutocompleteSearchOption,
  K8sAutocompleteSingleSelectChangeHandler,
  K8sAutocompleteSingleSelectInputChangeHandler,
  K8sTagLabelOptionSelectionChangeHandler
} from 'components/services/create/target-selection/kubernetes/types'

interface ServiceKubernetesTargetingSingleSelectionProps {
  selectedObject: K8sAutocompleteSearchOption | null
  onChange(option: K8sAutocompleteSearchOption | null): void
}

/**
 * A Kubernetes Targeting selection control which displays a categorized list of targets to select
 * from, along with an integrated searchable combo-box. Selections are single-select only.
 * Each category is comprised of an accordion with a virtualized list of objects.
 * Virtualized implementation based on MUI docs:
 * @see https://mui.com/material-ui/react-autocomplete/#virtualization
 */
export const ServiceKubernetesTargetingSingleSelection: React.FC<
  ServiceKubernetesTargetingSingleSelectionProps
> = ({ selectedObject, onChange }) => {
  const [autocompleteInputValue, setAutocompleteInputValue] = useState(
    selectedObject?.label ?? ''
  )

  const {
    data: k8sKinds,
    isError: isErrorKubernetesTargetsKinds,
    isLoading: isLoadingKubernetesTargetsKinds
  } = useGetKubernetesTargetsKinds({ placeholderData: [] })

  // List of options for the MUI Autocomplete component
  const { labelKeys: targetsByOwnerId, availableTargetParams } =
    useGetKubernetesTargetsByOwnerId()
  const options = useGetK8sOptionsList(targetsByOwnerId)
  const { categoryNames, categoryOptionsByName } = useGetGroupedK8sOptions(
    k8sKinds,
    targetsByOwnerId,
    options
  )

  const handleK8sTagLabelObjectChange: K8sTagLabelOptionSelectionChangeHandler =
    ({ target }, option) => {
      if (!target.checked) {
        onChange(null)
        setAutocompleteInputValue('')
        return
      }

      return onChange(option)
    }

  const handleAutocompleteChange: K8sAutocompleteSingleSelectChangeHandler = (
    _event,
    option
  ) => {
    onChange(option)
  }

  const handleAutocompleteInputChange: K8sAutocompleteSingleSelectInputChangeHandler =
    (_event, value: string) => {
      setAutocompleteInputValue(value)

      // This will catch conditions where we select a K8s object which also changes the
      // input field, and effectively fires `onChange` twice.
      if (value === selectedObject?.label) return

      onChange(null)
    }

  return (
    <Stack data-test-kubernetes-targeting-selection gap={2}>
      <Autocomplete<K8sAutocompleteSearchOption, false, false, false>
        {...getCommonK8sObjectsAutocompleteProps<false>(
          Boolean(selectedObject)
        )}
        data-testid="kubernetes-targeting-object-search"
        inputValue={autocompleteInputValue}
        options={options}
        value={selectedObject ?? null}
        onChange={handleAutocompleteChange}
        onInputChange={handleAutocompleteInputChange}
      />

      {availableTargetParams.size ? (
        <PageSection
          hasError={isErrorKubernetesTargetsKinds}
          isLoading={isLoadingKubernetesTargetsKinds}
          renderError={() => <ErrorSection css={{ padding: theme.space[2] }} />}
        >
          {categoryNames.map((categoryName) => (
            <KubernetesTagLabelSection
              key={categoryName}
              label={categoryName}
              options={categoryOptionsByName.get(categoryName) ?? []}
              selectedOptions={selectedObject ? [selectedObject] : []}
              onChange={handleK8sTagLabelObjectChange}
            />
          ))}
        </PageSection>
      ) : (
        <Alert severity="error">
          No Kubernetes clients available to target
        </Alert>
      )}
      <ServiceKubernetesTargetingSelectionFooter />
    </Stack>
  )
}
